import type { CatalogItemFull } from "@albertine/types/openapi/lmt";
import { useQuery } from "@tanstack/react-query";

export function useCatalogItemPlaceDetails(
    item: CatalogItemFull,
    googlePlaceService: google.maps.places.PlacesService,
) {
    return useQuery<PlaceDetails | undefined>({
        queryKey: [
            "catalogItemGooglePlace",
            { id: item.id, googlePlaceId: item.googlePlaceId },
        ],
        queryFn: () => fetchGooglePlace(item?.googlePlaceId ?? "", googlePlaceService),
    });
}

async function fetchGooglePlace(
    placeId: string,
    googlePlaceService: google.maps.places.PlacesService,
): Promise<PlaceDetails | undefined> {
    if (!placeId) {
        return undefined;
    }

    const request = {
        placeId,
        fields: [
            "business_status",
            "geometry",
            "rating",
            "reviews",
            "user_ratings_total",
            "opening_hours",
            "price_level",
            "types",
            // utc_offset_minutes is required for opening_hours.isOpen() to work
            // https://developers.google.com/maps/documentation/javascript/place_field_js_migration
            "utc_offset_minutes",
        ],
    };

    return new Promise((resolve) => {
        googlePlaceService.getDetails(request, (googlePlace) => {
            if (!googlePlace) {
                return resolve(undefined);
            }

            resolve({
                businessStatus: googlePlace.business_status,
                viewport: googlePlace.geometry?.viewport,
                reviews: {
                    rating: googlePlace.rating,
                    reviewsTotal: googlePlace.user_ratings_total,
                    googlePlaceId: placeId,
                },
                openingHours: googlePlace.opening_hours,
                priceLevel: googlePlace.price_level,
                types: googlePlace.types,
            });
        });
    });
}

type PlaceDetails = {
    businessStatus: google.maps.places.BusinessStatus | undefined;
    viewport: google.maps.LatLngBounds | undefined;
    reviews: {
        rating: number | undefined;
        reviewsTotal: number | undefined;
        googlePlaceId: string;
    };
    openingHours: google.maps.places.PlaceOpeningHours | undefined;
    priceLevel: number | undefined;
    types: string[] | undefined;
};
